/**
 * Phase 5 — Supplier portal performance diagnostics (development only).
 * No production behavior change when disabled (default off outside development).
 */

const STORAGE_KEY = 'tripsadora_supplier_perf';
const MAX_API_ENTRIES = 60;
const MAX_MARK_ENTRIES = 40;
const SLOW_MS = 2000;
const VERY_SLOW_MS = 5000;

const apiEntries = [];
const markEntries = [];
let runtimeEnabled = null;

function isDevBuild() {
  return process.env.NODE_ENV === 'development';
}

/** Dev-only unless explicitly toggled via env/localStorage. Production: always false. */
export function isSupplierPerfEnabled() {
  if (!isDevBuild()) return false;
  if (runtimeEnabled === false) return false;
  if (typeof process !== 'undefined' && process.env.NEXT_PUBLIC_SUPPLIER_PERF_DEBUG === '0') {
    return false;
  }
  if (typeof window !== 'undefined') {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === '0') return false;
      if (stored === '1') return true;
    } catch (_) {}
  }
  if (typeof process !== 'undefined' && process.env.NEXT_PUBLIC_SUPPLIER_PERF_DEBUG === '1') {
    return true;
  }
  return runtimeEnabled !== false;
}

export function setSupplierPerfEnabled(enabled) {
  if (!isDevBuild()) return;
  runtimeEnabled = !!enabled;
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0');
  } catch (_) {}
}

function pushRing(buffer, entry, max) {
  buffer.push(entry);
  if (buffer.length > max) buffer.shift();
}

function normalizeUrl(config) {
  const base = config.baseURL || '';
  const path = config.url || '';
  if (path.startsWith('http')) return path;
  return `${base}${path}`.replace(/([^:]\/)\/+/g, '$1');
}

function logSlowApi(entry) {
  const prefix = entry.level === 'very_slow' ? '[supplier-perf] VERY SLOW' : '[supplier-perf] slow';
  console.warn(`${prefix} ${entry.method} ${entry.url} — ${entry.durationMs}ms (${entry.status})`);
}

function logApiEntry(entry) {
  if (!isSupplierPerfEnabled()) return;
  pushRing(apiEntries, entry, MAX_API_ENTRIES);
  if (entry.level === 'slow' || entry.level === 'very_slow') {
    logSlowApi(entry);
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('supplier_perf_api', { detail: entry }));
  }
}

export function recordSupplierApiTiming(config, status) {
  if (!isSupplierPerfEnabled() || !config?.metadata?.supplierPerfStart) return null;

  const durationMs = Math.round(performance.now() - config.metadata.supplierPerfStart);
  const url = normalizeUrl(config);
  const method = (config.method || 'GET').toUpperCase();
  const level =
    durationMs >= VERY_SLOW_MS ? 'very_slow' : durationMs >= SLOW_MS ? 'slow' : 'ok';

  const entry = {
    kind: 'api',
    label: config.metadata.supplierPerfLabel || 'supplier',
    method,
    url,
    status,
    durationMs,
    level,
    at: Date.now(),
  };

  logApiEntry(entry);
  return entry;
}

export function attachSupplierAxiosPerf(axiosInstance, label = 'supplier-api') {
  if (!isDevBuild()) return;
  if (!axiosInstance?.interceptors) return;

  axiosInstance.interceptors.request.use((config) => {
    if (isSupplierPerfEnabled()) {
      config.metadata = {
        ...(config.metadata || {}),
        supplierPerfStart: performance.now(),
        supplierPerfLabel: label,
      };
    }
    return config;
  });

  axiosInstance.interceptors.response.use(
    (response) => {
      recordSupplierApiTiming(response.config, response.status);
      return response;
    },
    (error) => {
      if (error?.config) {
        recordSupplierApiTiming(
          error.config,
          error.response?.status ?? error.code ?? 'ERR',
        );
      }
      return Promise.reject(error);
    },
  );
}

export function supplierPerfMark(name, detail) {
  if (!isSupplierPerfEnabled()) return;
  try {
    performance.mark(name);
  } catch (_) {}
  const entry = { kind: 'mark', name, detail, at: Date.now() };
  pushRing(markEntries, entry, MAX_MARK_ENTRIES);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('supplier_perf_mark', { detail: entry }));
  }
}

export function supplierPerfMeasure(name, startMark, endMark) {
  if (!isSupplierPerfEnabled()) return null;
  try {
    performance.measure(name, startMark, endMark);
    const measure = performance.getEntriesByName(name, 'measure').pop();
    if (measure) {
      const entry = {
        kind: 'measure',
        name,
        durationMs: Math.round(measure.duration),
        at: Date.now(),
      };
      pushRing(markEntries, entry, MAX_MARK_ENTRIES);
      console.info(`[supplier-perf] ${name}: ${entry.durationMs}ms`);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('supplier_perf_mark', { detail: entry }));
      }
      return entry;
    }
  } catch (_) {}
  return null;
}

export function getSupplierPerfSnapshot() {
  return {
    enabled: isSupplierPerfEnabled(),
    api: [...apiEntries],
    marks: [...markEntries],
  };
}

export function dumpSupplierPerfTable() {
  if (!isSupplierPerfEnabled()) {
    console.info('[supplier-perf] diagnostics disabled (dev only)');
    return;
  }
  const snap = getSupplierPerfSnapshot();
  if (snap.api.length) {
    console.info('[supplier-perf] Recent API timings');
    console.table(
      snap.api.map((e) => ({
        label: e.label,
        method: e.method,
        ms: e.durationMs,
        status: e.status,
        level: e.level,
        url: e.url,
      })),
    );
  } else {
    console.info('[supplier-perf] No API timings recorded yet');
  }
  if (snap.marks.length) {
    console.info('[supplier-perf] Page markers / measures');
    console.table(
      snap.marks.map((e) => ({
        kind: e.kind,
        name: e.name,
        ms: e.durationMs ?? '',
        detail: e.detail ?? '',
      })),
    );
  }
}

export function installSupplierPerfConsoleHelpers() {
  if (typeof window === 'undefined' || !isDevBuild()) return;
  window.__supplierPerf = {
    dump: dumpSupplierPerfTable,
    snapshot: getSupplierPerfSnapshot,
    enable: () => setSupplierPerfEnabled(true),
    disable: () => setSupplierPerfEnabled(false),
    isEnabled: isSupplierPerfEnabled,
  };
}
